<template>
  <v-layout column>
    <v-row class="page-header-block">
      <h1 class="page-header-block__header">users</h1>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="users"
      :search="search"
      class="elevation-1 table--full-height"
    >
      <template v-slot:top>
        <v-toolbar flat color="white">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
          ></v-text-field>

          <v-spacer></v-spacer>

          <v-dialog v-model="dialog" class="dialog">
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="mb-2" v-bind="attrs" v-on="on" @click="resetDialog">Add User</v-btn>
            </template>

            <v-card>
              <validation-observer v-slot="{ handleSubmit }">
                <form @submit.prevent="handleSubmit(save)" v-if="dialog">
                  <v-card-title class="headline grey lighten-2" primary-title>
                    <span class="headline">{{ formTitle }}</span>
                  </v-card-title>

                  <v-card-text>
                    <v-container>
                      <warning :messages="warningMessages"></warning>

                      <v-row>
                        <v-col cols="6" v-if="!editedUser.id">
                          <validation-provider
                            :vid="uuid()"
                            name="Email"
                            :rules="`required|email|uniqueName:${existingEmails}`"
                            v-slot="{ errors }"
                          >
                            <v-text-field label="Email" v-model="editedUser.email"></v-text-field>

                            <span class="validation-message">{{ errors[0] }}</span>
                          </validation-provider>
                        </v-col>

                        <v-col
                          cols="6"
                          v-if="
                          !editedUser.id ||
                            (editedUser.id && updateType === 'info')
                        "
                        >
                          <validation-provider
                            :vid="uuid()"
                            name="Role"
                            rules="required"
                            v-slot="{ errors }"
                          >
                            <v-select label="Role" :items="roles" v-model="editedUser.role"></v-select>

                            <span class="validation-message">{{ errors[0] }}</span>
                          </validation-provider>
                        </v-col>

                        <v-col
                          cols="6"
                          v-if="
                          !editedUser.id ||
                            (editedUser.id && updateType === 'password')
                        "
                        >
                          <validation-provider
                            :vid="uuid()"
                            name="Passowrd"
                            rules="required|password:@Confirm|min:6|max:16"
                            v-slot="{ errors }"
                          >
                            <v-text-field
                              label="Password"
                              type="password"
                              v-model="editedUser.password"
                            ></v-text-field>

                            <span class="validation-message">{{ errors[0] }}</span>
                          </validation-provider>
                        </v-col>

                        <v-col
                          cols="6"
                          v-if="
                          !editedUser.id ||
                            (editedUser.id && updateType === 'password')
                        "
                        >
                          <validation-provider
                            name="Confirm"
                            rules="required|min:6|max:16"
                            v-slot="{ errors }"
                          >
                            <v-text-field
                              label="Confirm"
                              type="password"
                              v-model="editedUser.confirmPassword"
                            ></v-text-field>

                            <span class="validation-message">{{ errors[0] }}</span>
                          </validation-provider>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card-text>

                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="blue darken-1" text @click="resetDialog()">Cancel</v-btn>
                    <v-btn color="blue darken-1" text type="submit">Save</v-btn>
                  </v-card-actions>
                </form>
              </validation-observer>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-icon small class="mr-2" @click="editUser(item, 'info')">mdi-pencil</v-icon>
        <v-icon small class="mr-2" @click="editUser(item, 'password')">mdi-shield-lock</v-icon>
        <v-icon small @click="deleteUser(item)">mdi-delete</v-icon>
      </template>
    </v-data-table>
  </v-layout>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { assign, isEmpty, map } from "lodash";
import uuid from "src/mixins/uuid";
import validationRules from "src/mixins/validationRules";
import http from "src/util/http";
import Warning from "src/components/common/Warning";

export default {
  name: "Users",
  components: { ValidationObserver, ValidationProvider, Warning },
  mixins: [uuid, validationRules],
  data() {
    return {
      search: "",
      users: [],
      dialog: false,
      headers: [
        { text: "Email", value: "email" },
        { text: "Role", value: "role" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      roles: ["admin", "maintenance", "engineer", "programmer"],
      editedUser: {},
      updateType: "",
      warningMessages: [],
    };
  },
  computed: {
    formTitle() {
      return isEmpty(this.editedUser.id) ? "Add User" : "Edit User";
    },
    existingEmails() {
      return map(this.users, "email").join(";");
    },
  },
  mounted() {
    this.getUsers();
  },
  methods: {
    async getUsers() {
      this.$store.dispatch("updateShowLoading", true);

      const { data: users } = await http().get("users");
      this.users = users;

      this.$store.dispatch("updateShowLoading", false);
    },
    async deleteUser(user) {
      if (await this.$confirm("Are you sure you want to delete this user?")) {
        await http().delete(`users/${user.id}`);
        this.getUsers();
      }
    },
    async save() {
      if (this.editedUser.id) {
        await http().patch(`users/${this.editedUser.id}`, this.editedUser);
        this.resetDialog();
        await this.getUsers();
      } else {
        try {
          await http().post("users", this.editedUser);
          this.resetDialog();
          await this.getUsers();
        } catch (err) {
          this.warningMessages = ["Email exists"];
        }
      }
    },
    editUser(user, updateType) {
      this.resetDialog();
      this.editedUser = assign({}, user);
      this.dialog = true;
      this.updateType = updateType;
    },
    resetDialog() {
      this.dialog = false;
      this.editedUser = {};
      this.updateType = "";
      this.warningMessages = [];
    },
  },
};
</script>
